/**
 * Life4Earth V2 — Blog Article API Adapter
 *
 * Frontend client for the blog/knowledge article system.
 * Talks to:
 *   GET  /api/blog/articles            → list with filters
 *   GET  /api/blog/articles/{guid}     → single article
 */

import { apiFetch } from './client';

// ── Types ──────────────────────────────────────────────────────────

/** Compact article summary for list views and Impact tab cards. */
export interface BlogArticleSummary {
  articleGuid: string;
  slug: string;
  blogType: string;
  status: string;
  title: string;
  subtitle?: string;
  excerpt?: string;
  category?: string;
  tags?: string[];
  readingTimeMin?: number;
  featuredImageUrl?: string;
  authorGuid: string;
  authorDisplayName?: string;
  publishedAt?: string;
  createdAt: string;
}

/** Paginated list result from the articles endpoint. */
export interface BlogArticleListResult {
  articles: BlogArticleSummary[];
  total: number;
  page: number;
  pageSize: number;
}

/** Full article detail — includes body and citations. */
export interface BlogArticleDetail extends BlogArticleSummary {
  body: string;
  bodyFormat: string;
  language: string;
  featuredImageAlt?: string;
  communityGuids?: string[];
  reviewNotes?: string;
  submittedAt?: string;
  version: number;
  updatedAt: string;
  citations: BlogCitation[];
}

export interface BlogCitation {
  citationGuid: string;
  citationNumber: number;
  sourceTitle: string;
  sourceUrl?: string;
  sourceAuthor?: string;
  sourceDate?: string;
  quote?: string;
  notes?: string;
}

// ── API Functions ──────────────────────────────────────────────────

/**
 * Fetch published blog articles authored by a specific user.
 * Used on the Impact tab to show "My Knowledge" contributions.
 *
 * @param blogType  Optional blog type filter (e.g. 'how_to', 'best_practice').
 *                  When omitted, returns all types.
 */
export async function fetchUserArticles(
  userGuid: string,
  blogType?: string,
): Promise<BlogArticleSummary[]> {
  const params: Record<string, string> = {
    AuthorGuid: userGuid,
    Status: 'published',
    PageSize: '50',
  };
  if (blogType) params.BlogType = blogType;

  const qs = new URLSearchParams(params);
  const result = await apiFetch<BlogArticleListResult>(
    `/api/blog/articles?${qs.toString()}`,
  );
  return result.articles ?? [];
}

/**
 * Fetch user's draft blog articles (for the Knowledge tab on profile).
 *
 * @param blogType  Optional blog type filter.
 */
export async function fetchUserDraftArticles(
  userGuid: string,
  blogType?: string,
): Promise<BlogArticleSummary[]> {
  const params: Record<string, string> = {
    AuthorGuid: userGuid,
    Status: 'draft',
    PageSize: '50',
  };
  if (blogType) params.BlogType = blogType;

  const qs = new URLSearchParams(params);
  const result = await apiFetch<BlogArticleListResult>(
    `/api/blog/articles?${qs.toString()}`,
  );
  return result.articles ?? [];
}

/**
 * Fetch published articles for a specific blog type across all authors.
 * Used on community-level knowledge feeds (e.g. "Community How-Tos").
 */
export async function fetchArticlesByType(
  blogType: string,
  page = 1,
  pageSize = 20,
): Promise<BlogArticleListResult> {
  const qs = new URLSearchParams({
    BlogType: blogType,
    Status: 'published',
    Page: String(page),
    PageSize: String(pageSize),
  });
  return apiFetch<BlogArticleListResult>(
    `/api/blog/articles?${qs.toString()}`,
  );
}

/** Fetch a single article by GUID (full detail including body). */
export function fetchArticle(guid: string): Promise<BlogArticleDetail> {
  return apiFetch<BlogArticleDetail>(`/api/blog/articles/${guid}`);
}
